'use client';
import { Button, useToast } from '@ethsign/ui';
import classNames from 'classnames';

import { ChangeEvent, FC, LegacyRef, useRef, useState } from 'react';
import { DndProvider, DropTargetMonitor, useDrop } from 'react-dnd';

import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend';

import { UploadBigIcon, UploadIcon } from '@/components';
import { getFileExtension } from '@/utils';
const ACCEPT_FILE_EXTENSION = '.pdf';
const MAX_TOTAL_SIZE = 5 * 1000 * 1000;

export interface IProps {
  mode?: 'view1' | 'view2';
  className?: string;
  onUpload?: (files: File[]) => void;
  children?: any;
}

const FileUpload: FC<IProps> = ({ className, onUpload, children }) => {
  const [error, setError] = useState<string>();
  const fileUploadRef = useRef<HTMLInputElement>();
  const { toast } = useToast();
  const handleUpload = (files: File[]) => {
    if (
      Array.from(files).find((file) => {
        return ACCEPT_FILE_EXTENSION.indexOf(getFileExtension(file.name)) === -1;
      })
    ) {
      toast({
        title: 'Upload Error',
        description: <div>File type of uploaded document is unsupported</div>,
        variant: 'error'
      });
      return;
    }

    if (Array.from(files).some((file) => file.size > 5 * 1024 * 1024)) {
      toast({
        title: 'Upload Error',
        description: (
          <div>
            <div className="text-sm font-medium text-red-700">Document Size is Too Large</div>Please upload a PDF file
            under 5MB.
          </div>
        ),
        variant: 'error'
      });
      return;
    }

    const totalSize = Array.from(files).reduce((prev: number, current: File) => prev + current.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      setError('File is too large. Maximum file size allowed is 25MB');
      return;
    }

    onUpload &&
      onUpload(
        Array.from(files).map((file) => {
          (file as any).uploadTime = new Date();
          return file;
        })
      );
  };

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item: { files: File[] }) {
        handleUpload(item.files);
      },
      canDrop() {
        return true;
      },
      collect: (monitor: DropTargetMonitor) => {
        return {
          isOver: monitor.isOver(),
          canDrop: monitor.canDrop()
        };
      }
    }),
    [onUpload]
  );

  const hasAvailableAndValidFiles = (e: ChangeEvent<HTMLInputElement>): boolean => {
    return Boolean(e?.target?.files?.length);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const hasFiles = hasAvailableAndValidFiles(e);

    if (!hasFiles) {
      return;
    }

    const files = Object.values(e.target?.files as FileList);
    e.target.value = '';
    handleUpload(files);
  };

  const handleFileInputClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    setError(undefined);
    fileUploadRef.current?.click();
  };

  const isActive = canDrop && isOver;
  return children ? (
    <div onClick={handleFileInputClick}>
      {children}
      <input
        ref={fileUploadRef as LegacyRef<HTMLInputElement>}
        hidden
        accept={ACCEPT_FILE_EXTENSION}
        className="cursor-pointer"
        id="contained-button-file"
        type="file"
        data-testid="file-upload-area-input"
        onChange={handleFileUpload}
      />
    </div>
  ) : (
    <div
      ref={drop as any}
      className={classNames(
        'relative w-full px-6 flex flex-col items-center justify-center cursor-pointer h-[400px] border border-gray-100 rounded-2xl',
        className
      )}
      data-testid="file-upload-area"
      onClick={handleFileInputClick}
    >
      {!isActive ? (
        <>
          <div className=" flex justify-center items-cente">
            <UploadBigIcon />
          </div>
          <div className="mt-6 text-md font-semiBold text-gray-900'">
            <span>Drag & drop PDF file or </span>
            <label className="text-primary-900 cursor-pointer">
              <input
                ref={fileUploadRef as LegacyRef<HTMLInputElement>}
                hidden
                accept={ACCEPT_FILE_EXTENSION}
                className="cursor-pointer"
                id="contained-button-file"
                type="file"
                data-testid="file-upload-area-input"
                onChange={handleFileUpload}
              />
              Browse
            </label>
            <span> to get started</span>
          </div>
          <span className="text-xs font-normal text-gray-400 mt-2">Upload a PDF file. Max file size is 5MB</span>
          {error && (
            <span className="text-sm text-center text-red-500 font-bold mt-2" data-testid="file-upload-area-error">
              {error}
            </span>
          )}
          <div className="mt-8">
            <Button size="lg" className="border-none">
              <span className="mr-2">
                <UploadIcon />
              </span>
              Upload Now
            </Button>
          </div>
        </>
      ) : (
        <div className="h-[136px] flex items-center justify-center bg-white bg-opacity-80 ">
          <span className="text-center text-gray-600 text-2xl">Drop here...</span>
        </div>
      )}
    </div>
  );
};

export const FileUploadArea: FC<IProps> = (props) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <FileUpload {...props}></FileUpload>
    </DndProvider>
  );
};
